import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

// Critical-path pages — loaded eagerly so first-visit render has no waterfall
import Home from "./pages/Home";
import Memorial from "./pages/Memorial";
import MemorialUpload from "./pages/MemorialUpload";
import MemorialImages from "./pages/MemorialImages";
import MemorialNotes from "./pages/MemorialNotes";
import MyUploads from "./pages/MyUploads";
import Navbar from "./components/Navbar";
import EdgeBlur from "./components/EdgeBlur";
import FAB from "./components/FAB";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { useCurrentMemorial } from "./context/CurrentMemorialContext";

// Non-critical pages — lazy-loaded so they don't bloat the initial bundle
const AdminLogin          = lazy(() => import("./pages/AdminLogin"));
const Activate            = lazy(() => import("./pages/Activate"));
const SuperAdminLogin     = lazy(() => import("./pages/SuperAdminLogin"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const Legal               = lazy(() => import("./pages/Legal"));

/**
 * A thin wrapper around the app's routes that fades/slides the whole
 * page tree on every navigation. Kept intentionally short (~200ms) so
 * nav taps still feel instant but gain a subtle sense of motion.
 */
function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      >
        <Suspense fallback={null}>
          <Routes location={location}>
            {/* The landing page answers every arrival that isn't a live memorial.
                /s/:code is what the engraved QR points at; /activate/:code is kept
                forever for signs from the first batches. Most in-store scans are
                curiosity, so a scan lands on the explainer — never straight in the
                signup form. The wizard itself lives at /setup/:code. */}
            <Route path="/" element={<Home />} />
            <Route path="/s/:code" element={<Home />} />
            <Route path="/activate/:code" element={<Home />} />
            <Route path="/setup/:code" element={<Activate />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/glimt-login" element={<SuperAdminLogin />} />
            <Route path="/glimt-dashboard" element={<SuperAdminDashboard />} />
            <Route path="/m/:slug" element={<Memorial />} />
            <Route path="/m/:slug/images" element={<MemorialImages />} />
            <Route path="/m/:slug/notes" element={<MemorialNotes />} />
            <Route path="/m/:slug/upload" element={<MemorialUpload />} />
            <Route path="/m/:slug/my-uploads" element={<MyUploads />} />
            <Route path="/legal/:doc" element={<Legal />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Routes where no chrome at all should render
 * (landing, activation flow, auth screens, super-admin console).
 */
function shouldHideShell(pathname) {
  if (pathname === '/') return true;
  if (pathname.startsWith('/activate/')) return true;
  if (pathname.startsWith('/setup/')) return true;
  if (/^\/s\//i.test(pathname)) return true;
  if (pathname === '/admin/login') return true;
  if (pathname === '/glimt-login') return true;
  if (pathname === '/glimt-dashboard') return true;
  if (pathname.startsWith('/legal/')) return true;
  return false;
}

/**
 * Extract the memorial slug from any `/m/:slug/*` path. Returns null
 * if we're not inside a memorial route.
 */
function getMemorialSlug(pathname) {
  const m = pathname.match(/^\/m\/([^/]+)(?:\/.*)?$/);
  return m ? m[1] : null;
}

/**
 * Renders the persistent chrome — edge blur, pill nav, and the
 * floating action button — OUTSIDE `AnimatePresence` so each piece
 * stays mounted across route changes.
 *
 * On the upload page we hide the header/nav/edge-blur and morph the
 * FAB into an X (same element, rotated 45°) so it reads as a single
 * continuous object whose role has shifted from "add" to "close".
 */
function PersistentShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug: currentSlug } = useCurrentMemorial();

  if (shouldHideShell(location.pathname)) return null;

  const slug = getMemorialSlug(location.pathname) || currentSlug;
  const onUpload = !!slug && location.pathname === `/m/${slug}/upload`;
  const showChrome = !onUpload;
  const showFAB = !!slug;

  const handleFabClick = () => {
    if (!slug) return;
    if (onUpload) navigate(`/m/${slug}`);
    else navigate(`/m/${slug}/upload`);
  };

  return (
    <>
      {showChrome && <EdgeBlur />}
      {showChrome && <Navbar />}
      {showFAB && (
        <FAB
          mode={onUpload ? 'close' : 'add'}
          onClick={handleFabClick}
          ariaLabel={onUpload ? 'Close upload' : 'Add memory'}
        />
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <AnimatedRoutes />
      <PersistentShell />
      <CookieConsentBanner />
    </Router>
  );
}

export default App;
